import React, { useState, useEffect } from 'react';
import { Player, PlayerUpdateInput } from '../../services/playersService';

interface EditPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PlayerUpdateInput) => Promise<void>;
  player: Player | null;
}

const EditPlayerModal: React.FC<EditPlayerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  player
}) => {
  const [formData, setFormData] = useState<PlayerUpdateInput>({
    full_name: '',
    position: null,
    date_of_birth: null,
    nationality: null,
    profile_image: null,
    height_cm: null,
    weight_kg: null,
    preferred_foot: null,
    kit_number: null,
    bio: null,
    social_media: null,
    contact_info: null,
    is_active: true,
    data_source: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when player changes
  useEffect(() => {
    if (player) {
      setFormData({
        full_name: player.full_name,
        position: player.position,
        date_of_birth: player.date_of_birth,
        nationality: player.nationality,
        profile_image: player.profile_image,
        height_cm: player.height_cm,
        weight_kg: player.weight_kg,
        preferred_foot: player.preferred_foot,
        kit_number: player.kit_number,
        bio: player.bio,
        social_media: player.social_media,
        contact_info: player.contact_info,
        is_active: player.is_active,
        data_source: player.data_source
      });
      setErrors({});
    }
  }, [player]);

  const handleInputChange = (field: keyof PlayerUpdateInput, value: string | boolean | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Player name is required';
    }

    if (!formData.data_source?.trim()) {
      newErrors.data_source = 'Data source is required';
    }

    // Validate kit number if provided
    if (formData.kit_number !== null && formData.kit_number !== undefined) {
      if (formData.kit_number < 1 || formData.kit_number > 99) {
        newErrors.kit_number = 'Kit number must be between 1 and 99';
      }
    }

    // Validate height if provided
    if (formData.height_cm !== null && formData.height_cm !== undefined) {
      if (formData.height_cm < 100 || formData.height_cm > 250) {
        newErrors.height_cm = 'Height must be between 100 and 250 cm';
      }
    }

    // Validate weight if provided
    if (formData.weight_kg !== null && formData.weight_kg !== undefined) {
      if (formData.weight_kg < 30 || formData.weight_kg > 200) {
        newErrors.weight_kg = 'Weight must be between 30 and 200 kg';
      }
    }

    // Validate date if provided
    if (formData.date_of_birth) {
      const date = new Date(formData.date_of_birth);
      if (isNaN(date.getTime())) {
        newErrors.date_of_birth = 'Invalid date format';
      } else if (date > new Date()) {
        newErrors.date_of_birth = 'Date of birth cannot be in the future';
      }
    }

    // Normalize JSON fields if user typed strings
    const normalizeJson = (val: any) => {
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          return JSON.parse(val);
        } catch (_e) {
          return val; // keep as string; DB will reject if invalid
        }
      }
      return val;
    };

    formData.social_media = normalizeJson(formData.social_media) as any;
    formData.contact_info = normalizeJson(formData.contact_info) as any;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error saving player:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !player) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Player</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Player Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Player Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter player name"
              />
              {errors.full_name && (
                <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>
              )}
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <select
                value={formData.position || ''}
                onChange={(e) => handleInputChange('position', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select position...</option>
                <option value="Goalkeeper">Goalkeeper</option>
                <option value="Defender">Defender</option>
                <option value="Midfielder">Midfielder</option>
                <option value="Forward">Forward</option>
              </select>
            </div>

            {/* Jersey Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jersey Number
              </label>
              <input
                type="number"
                min="1"
                max="99"
                value={formData.kit_number || ''}
                onChange={(e) => handleInputChange('kit_number', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter jersey number"
              />
              {errors.kit_number && (
                <p className="text-red-500 text-xs mt-1">{errors.kit_number}</p>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.date_of_birth || ''}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
              )}
            </div>

            {/* Nationality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nationality
              </label>
              <input
                type="text"
                value={formData.nationality || ''}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter nationality"
              />
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (cm)
              </label>
              <input
                type="number"
                min="100"
                max="250"
                value={formData.height_cm || ''}
                onChange={(e) => handleInputChange('height_cm', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter height in cm"
              />
              {errors.height_cm && (
                <p className="text-red-500 text-xs mt-1">{errors.height_cm}</p>
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                min="30"
                max="200"
                value={formData.weight_kg || ''}
                onChange={(e) => handleInputChange('weight_kg', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter weight in kg"
              />
              {errors.weight_kg && (
                <p className="text-red-500 text-xs mt-1">{errors.weight_kg}</p>
              )}
            </div>

            {/* Data Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Source *
              </label>
              <select
                value={formData.data_source}
                onChange={(e) => handleInputChange('data_source', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select data source...</option>
                <option value="official">Official</option>
                <option value="custom">Custom</option>
              </select>
              {errors.data_source && (
                <p className="text-red-500 text-xs mt-1">{errors.data_source}</p>
              )}
            </div>
          </div>

          {/* Optional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preferred Foot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Foot</label>
              <select
                value={formData.preferred_foot || ''}
                onChange={(e) => handleInputChange('preferred_foot', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* Profile Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profile Image URL</label>
              <input
                type="text"
                value={formData.profile_image || ''}
                onChange={(e) => handleInputChange('profile_image', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            {/* Social Media (JSON) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Social Media (JSON)</label>
              <textarea
                value={typeof formData.social_media === 'string' ? formData.social_media : (formData.social_media ? JSON.stringify(formData.social_media, null, 2) : '')}
                onChange={(e) => handleInputChange('social_media', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                placeholder='{"twitter":"@handle"}'
              />
              {errors.social_media && (
                <p className="text-red-500 text-xs mt-1">{errors.social_media}</p>
              )}
            </div>

            {/* Contact Info (JSON) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Info (JSON)</label>
              <textarea
                value={typeof formData.contact_info === 'string' ? formData.contact_info : (formData.contact_info ? JSON.stringify(formData.contact_info, null, 2) : '')}
                onChange={(e) => handleInputChange('contact_info', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                placeholder='{"phone":"+1..."}'
              />
              {errors.contact_info && (
                <p className="text-red-500 text-xs mt-1">{errors.contact_info}</p>
              )}
            </div>

            {/* Bio */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={formData.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Short player bio"
              />
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Active Status
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Saving...' : 'Update Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPlayerModal;
